import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin } from 'lucide-react';
import { useGalleryConfig } from '../../context/GalleryConfigContext';
import { useGalleryTheme } from '../../context/GalleryThemeContext';

const GalleryFooter: React.FC = () => {
  const { config } = useGalleryConfig();
  const { theme } = useGalleryTheme();

  if (!config) return null;

  return (
    <footer
      className="mt-auto py-8 border-t"
      style={{
        backgroundColor: theme.colors.surface,
        borderTopColor: theme.colors.border,
      }}
    >
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Business Info */}
          <div>
            <div className="flex items-center space-x-3 mb-4">
              {config.branding.logo ? (
                <img
                  src={config.branding.logo}
                  alt={`${config.businessName} Logo`}
                  className="h-8 w-auto"
                />
              ) : (
                <div
                  className="h-8 w-8 rounded flex items-center justify-center text-white font-bold"
                  style={{ backgroundColor: theme.colors.primary }}
                >
                  {config.businessName.charAt(0).toUpperCase()}
                </div>
              )}
              <span className="font-bold text-lg" style={{ color: theme.colors.text.primary }}>
                {config.businessName}
              </span>
            </div>

            {config.seo.description && (
              <p className="text-sm mb-4" style={{ color: theme.colors.text.secondary }}>
                {config.seo.description}
              </p>
            )}

            {/* Contact Info */}
            {config.seo.structuredData.contactPoint && (
              <div className="space-y-2 text-sm">
                {config.seo.structuredData.contactPoint.email && (
                  <div
                    className="flex items-center space-x-2"
                    style={{ color: theme.colors.text.secondary }}
                  >
                    <Mail size={16} />
                    <a
                      href={`mailto:${config.seo.structuredData.contactPoint.email}`}
                      className="hover:underline"
                      style={{ color: theme.colors.text.secondary }}
                    >
                      {config.seo.structuredData.contactPoint.email}
                    </a>
                  </div>
                )}

                {config.seo.structuredData.contactPoint.telephone && (
                  <div
                    className="flex items-center space-x-2"
                    style={{ color: theme.colors.text.secondary }}
                  >
                    <Phone size={16} />
                    <a
                      href={`tel:${config.seo.structuredData.contactPoint.telephone}`}
                      className="hover:underline"
                      style={{ color: theme.colors.text.secondary }}
                    >
                      {config.seo.structuredData.contactPoint.telephone}
                    </a>
                  </div>
                )}

                {config.seo.structuredData.contactPoint.website && (
                  <div
                    className="flex items-center space-x-2"
                    style={{ color: theme.colors.text.secondary }}
                  >
                    <MapPin size={16} />
                    <a
                      href={config.seo.structuredData.contactPoint.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:underline"
                      style={{ color: theme.colors.text.secondary }}
                    >
                      Visit Website
                    </a>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-semibold mb-4" style={{ color: theme.colors.text.primary }}>
              Quick Links
            </h4>
            <ul className="space-y-2 text-sm">
              <li>
                <Link
                  to="/"
                  className="hover:underline"
                  style={{ color: theme.colors.text.secondary }}
                >
                  Home
                </Link>
              </li>
              <li>
                <Link
                  to="/events"
                  className="hover:underline"
                  style={{ color: theme.colors.text.secondary }}
                >
                  Galleries
                </Link>
              </li>
              <li>
                <Link
                  to="/cart"
                  className="hover:underline"
                  style={{ color: theme.colors.text.secondary }}
                >
                  Shopping Cart
                </Link>
              </li>
              {config.settings.showPhotographer && (
                <li>
                  <a
                    href="#about"
                    className="hover:underline"
                    style={{ color: theme.colors.text.secondary }}
                  >
                    About
                  </a>
                </li>
              )}
            </ul>
          </div>

          {/* Legal Links */}
          <div>
            <h4 className="font-semibold mb-4" style={{ color: theme.colors.text.primary }}>
              Legal
            </h4>
            <div className="space-y-2 text-sm">
              <div>
                <a
                  href="/privacy"
                  className="hover:underline"
                  style={{ color: theme.colors.text.secondary }}
                >
                  Privacy Policy
                </a>
              </div>
              <div>
                <a
                  href="/terms"
                  className="hover:underline"
                  style={{ color: theme.colors.text.secondary }}
                >
                  Terms of Service
                </a>
              </div>
              <div>
                <a
                  href="/contact"
                  className="hover:underline"
                  style={{ color: theme.colors.text.secondary }}
                >
                  Contact Us
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div
          className="mt-8 pt-8 border-t text-center text-sm"
          style={{
            borderTopColor: theme.colors.border,
            color: theme.colors.text.muted,
          }}
        >
          <p>
            © {new Date().getFullYear()} {config.businessName}. All rights reserved.
          </p>

          {/* Pixel Share Branding (configurable) */}
          {config.branding.showWatermark && (
            <p className="mt-2">
              Powered by{' '}
              <a
                href="https://pixel-share.com"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:underline"
                style={{ color: theme.colors.primary }}
              >
                Pixel Share
              </a>
            </p>
          )}
        </div>
      </div>
    </footer>
  );
};

export default GalleryFooter;
